import {
  Alert,
  Anchor,
  Badge,
  Button,
  Divider,
  Grid,
  Group,
  List,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconCalculator,
  IconExternalLink,
  IconInfoCircle,
  IconListDetails,
  IconRefresh,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
  IconTruckDelivery,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import {
  calculateAdvertisingScenario,
  STANDARD_CONVERSION_RATES,
} from '../features/amazon/advertising-scenarios';
import {
  AMAZON_FEE_GST_RATE,
  AMAZON_RETURN_FEE_CARD,
  calculateContribution,
  calculateDimensionThresholds,
  calculateEasyShipFee,
  calculateFbaPickPackFee,
  calculateFbaWeightHandlingFee,
  calculateMarginPriceTarget,
  checkHeavyBulky,
  EASY_SHIP_RATE_CARD,
  FBA_RATE_CARD,
  fromCentimeters,
  getAdjacentWeightSlabScenarios,
  type AdjacentWeightSlabScenario,
  type ContributionEstimate,
  type ContributionInput,
  type DimensionThresholdOutcome,
  type DimensionThresholdResult,
  type DimensionUnit,
  type FbaShippingZone,
  type FulfillmentChannel,
  type MarginPriceTarget,
  type StepLevel,
  toCentimeters,
  validateContributionInput,
  WALL_ART_FEE_CARD,
  WALL_ART_PRODUCT_GST_RATE,
} from '../features/amazon/easy-ship';
import {
  amazonProductPayload,
  calculatorStateFromProduct,
  initialCalculatorState,
  type CalculatorForm,
  type NumericValue,
  type SavedAmazonProduct,
} from '../features/amazon/saved-product';
import { apiHook } from '../treaty';

const moneyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 3,
});

const marginTargets = [10, 20, 30, 40] as const;
export type AmazonCalculatorAccessMode = 'guest' | 'authenticated';

const officialSources = [
  {
    title: 'Amazon India fees and pricing',
    description: 'Rate card, volumetric-weight formula, size rules, GST, and distance definitions.',
    url: 'https://sell.amazon.in/fees-and-pricing',
  },
  {
    title: 'Amazon Easy Ship overview',
    description: 'How Easy Ship pickup, delivery, tracking, and Pay on Delivery work.',
    url: 'https://sell.amazon.in/shipping-and-fulfillment/easy-ship?lang=en-IN',
  },
  {
    title: 'Fulfillment by Amazon overview',
    description: 'How Amazon stores, packs, ships, and supports FBA orders.',
    url: 'https://sell.amazon.in/shipping-and-fulfillment/fulfillment-by-amazon',
  },
  {
    title: 'Amazon STEP program',
    description: 'STEP levels, benefits, and the Standard starting level for new sellers.',
    url: 'https://sell.amazon.in/grow-your-business/step',
  },
  {
    title: 'March 2026 Amazon fee update',
    description: 'Amazon’s announcement of the latest India seller-fee changes.',
    url: 'https://www.aboutamazon.in/news/small-business/amazon-seller-fee-reduction-zero-referral-march-2026',
  },
  {
    title: 'November 2025 return-fee update',
    description: 'Refund-fee rate card and the policy retaining closing and weight-handling fees.',
    url: AMAZON_RETURN_FEE_CARD.sourceUrl,
  },
] as const;

function numeric(value: NumericValue): number {
  return typeof value === 'number' ? value : value.trim() === '' ? Number.NaN : Number(value);
}

function isProvided(value: NumericValue): boolean {
  return typeof value === 'number' || value.trim() !== '';
}

function signedMoney(value: number): string {
  return `${value > 0 ? '+' : ''}${moneyFormatter.format(value)}`;
}

function thresholdOutcomesMatch(
  first: DimensionThresholdOutcome,
  second: DimensionThresholdOutcome,
): boolean {
  return (
    first.status === second.status &&
    first.value === second.value &&
    first.resultingSlab === second.resultingSlab &&
    first.message === second.message &&
    (first.amazonFeeChange === second.amazonFeeChange ||
      (first.amazonFeeChange !== null &&
        second.amazonFeeChange !== null &&
        Math.abs(first.amazonFeeChange - second.amazonFeeChange) < 0.005)) &&
    (first.contributionChange === second.contributionChange ||
      (first.contributionChange !== null &&
        second.contributionChange !== null &&
        Math.abs(first.contributionChange - second.contributionChange) < 0.005))
  );
}

function DimensionThresholdHint({
  direction,
  outcomes,
}: {
  direction: 'previous' | 'next';
  outcomes: DimensionThresholdResult['previous'];
}) {
  const isPrevious = direction === 'previous';
  const combined = thresholdOutcomesMatch(outcomes.easyShip, outcomes.fba);
  const items = combined
    ? [{ label: 'Easy Ship & FBA', outcome: outcomes.easyShip }]
    : [
        { label: 'Easy Ship', outcome: outcomes.easyShip },
        { label: 'FBA', outcome: outcomes.fba },
      ];

  return (
    <Stack gap={1}>
      {items.map(({ label, outcome }) => {
        const valueText =
          outcome.status === 'value' && outcome.value !== null
            ? `${isPrevious ? '≤' : '≥'} ${outcome.value.toFixed(2)} ${
                outcome.unit === 'cm' ? 'cm' : 'in'
              } → ${outcome.resultingSlab}`
            : outcome.message;
        const changeText =
          outcome.amazonFeeChange !== null && outcome.contributionChange !== null
            ? ` · fees ${signedMoney(outcome.amazonFeeChange)} · contribution ${signedMoney(
                outcome.contributionChange,
              )}`
            : '';

        return (
          <Text
            key={label}
            size='xs'
            c={isPrevious ? 'green.7' : outcome.status === 'heavyBulky' ? 'red.7' : 'orange.8'}
            lh={1.25}
          >
            <Text span fw={700}>
              {label}:
            </Text>{' '}
            {valueText}
            {changeText}
          </Text>
        );
      })}
    </Stack>
  );
}

function MarginTargetMethod({
  label,
  target,
  currentPrice,
}: {
  label: string;
  target: MarginPriceTarget | null;
  currentPrice: number;
}) {
  if (!target) {
    return (
      <div>
        <Text size='xs' c='dimmed'>
          {label}
        </Text>
        <Text size='sm' fw={700} c='red'>
          Not reachable
        </Text>
      </div>
    );
  }

  const priceDifference = target.sellingPrice - currentPrice;
  return (
    <div>
      <Text size='xs' c='dimmed'>
        {label}
      </Text>
      <Text size='lg' fw={800}>
        {moneyFormatter.format(target.sellingPrice)}
      </Text>
      <Text size='xs' c={priceDifference > 0 ? 'orange.8' : 'green.7'}>
        {priceDifference === 0
          ? 'Current price'
          : `${signedMoney(priceDifference)} vs current · ${target.estimate.expectedMargin.toFixed(
              1,
            )}% achieved`}
      </Text>
    </div>
  );
}

function ResultRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Text size='sm' fw={strong ? 700 : 400}>
          {label}
        </Text>
      </Table.Td>
      <Table.Td ta='right'>
        <Text size='sm' fw={strong ? 700 : 600}>
          {value}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

function WeightScenarioCard({
  direction,
  scenario,
  estimate,
  input,
}: {
  direction: 'previous' | 'next';
  scenario: AdjacentWeightSlabScenario | null;
  estimate: ContributionEstimate;
  input: ContributionInput;
}) {
  const isPrevious = direction === 'previous';
  const title = isPrevious ? 'Previous weight slab' : 'Next weight slab';
  const icon = isPrevious ? <IconArrowDown size={18} /> : <IconArrowUp size={18} />;

  if (!scenario) {
    return (
      <Paper withBorder radius='md' p='md'>
        <Group gap='xs' mb='xs'>
          <ThemeIcon color='green' variant='light' size='md'>
            {icon}
          </ThemeIcon>
          <Text fw={700}>{title}</Text>
        </Group>
        <Text size='sm' c='dimmed'>
          Already in the lowest Easy Ship weight slab.
        </Text>
      </Paper>
    );
  }

  const shipping =
    input.fulfillmentChannel === 'fba'
      ? calculateFbaWeightHandlingFee(
          scenario.chargeableWeightKg,
          input.stepLevel,
          input.fbaShippingZone,
        )
      : calculateEasyShipFee(scenario.chargeableWeightKg, input.stepLevel, input.sellingPrice);
  const shippingDifference = shipping.fee - estimate.easyShipFee;
  const projectedPickPack =
    input.fulfillmentChannel === 'fba' ? calculateFbaPickPackFee(scenario.chargeableWeightKg) : 0;
  const pickPackDifference = projectedPickPack - estimate.fbaPickPackFee;
  const feeDifference = shippingDifference + pickPackDifference;
  const projectedAmazonFeeSubtotal = estimate.amazonFeeSubtotal + feeDifference;
  const projectedAmazonFeeGst = projectedAmazonFeeSubtotal * AMAZON_FEE_GST_RATE;
  const projectedAmazonCashFees = projectedAmazonFeeSubtotal + projectedAmazonFeeGst;
  const projectedGstPayable = Math.max(estimate.productGst - projectedAmazonFeeGst, 0);
  const deliveredCostDifference =
    projectedAmazonCashFees +
    projectedGstPayable -
    estimate.amazonCashFees -
    estimate.gstPayableAfterAmazonFeeItc;
  const returnedCostDifference = feeDifference * (1 + AMAZON_FEE_GST_RATE);
  const returnRate = estimate.returnRatePercentage / 100;
  const expectedCostDifference =
    deliveredCostDifference * (1 - returnRate) + returnedCostDifference * returnRate;
  const projectedContribution = estimate.expectedContribution - expectedCostDifference;
  const projectedMargin = (projectedContribution / estimate.netProductRevenue) * 100;

  return (
    <Paper withBorder radius='md' p='md'>
      <Group justify='space-between' align='flex-start' mb='sm'>
        <Group gap='xs'>
          <ThemeIcon color={isPrevious ? 'green' : 'orange'} variant='light' size='md'>
            {icon}
          </ThemeIcon>
          <div>
            <Text fw={700}>{title}</Text>
            <Text size='xs' c='dimmed'>
              {scenario.comparisonLabel}
            </Text>
          </div>
        </Group>
        <Badge color={feeDifference > 0 ? 'orange' : 'green'} variant='light'>
          {feeDifference > 0 ? '+' : ''}
          {moneyFormatter.format(feeDifference)}
        </Badge>
      </Group>
      <Stack gap={5}>
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            Shipping charge
          </Text>
          <Text size='sm' fw={600}>
            {moneyFormatter.format(shipping.fee)}
          </Text>
        </Group>
        {input.fulfillmentChannel === 'fba' && (
          <Group justify='space-between'>
            <Text size='sm' c='dimmed'>
              Pick & pack
            </Text>
            <Text size='sm' fw={600}>
              {moneyFormatter.format(projectedPickPack)}
            </Text>
          </Group>
        )}
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            Expected cost change after capped ITC
          </Text>
          <Text size='sm' fw={600} c={expectedCostDifference > 0 ? 'red' : 'green'}>
            {expectedCostDifference > 0 ? '+' : ''}
            {moneyFormatter.format(expectedCostDifference)}
          </Text>
        </Group>
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            Projected contribution
          </Text>
          <Text size='sm' fw={700}>
            {moneyFormatter.format(projectedContribution)}
          </Text>
        </Group>
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            Projected margin
          </Text>
          <Text size='sm' fw={700}>
            {projectedMargin.toFixed(1)}%
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
}

function responseMessage(error: unknown, fallback: string) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'value' in error &&
    typeof error.value === 'object' &&
    error.value !== null &&
    'message' in error.value
  ) {
    return String(error.value.message);
  }
  return fallback;
}

function AmazonCalculator({
  accessMode,
  savedProduct,
}: {
  accessMode: AmazonCalculatorAccessMode;
  savedProduct: SavedAmazonProduct | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManageProducts = accessMode === 'authenticated';
  const savedState = savedProduct
    ? calculatorStateFromProduct(savedProduct)
    : initialCalculatorState;
  const [productName, setProductName] = useState(savedState.name);
  const [stepLevel, setStepLevel] = useState<StepLevel>(savedState.stepLevel);
  const [fbaShippingZone, setFbaShippingZone] = useState<FbaShippingZone>(
    savedState.fbaShippingZone,
  );
  const [fbaShipInOwnContainer, setFbaShipInOwnContainer] = useState(
    savedState.fbaShipInOwnContainer,
  );
  const [dimensionUnit, setDimensionUnit] = useState<DimensionUnit>(savedState.dimensionUnit);
  const [form, setForm] = useState<CalculatorForm>(savedState.form);
  const [saving, setSaving] = useState(false);

  function updateField(field: keyof CalculatorForm, value: NumericValue) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function displayedDimension(value: NumericValue): NumericValue {
    if (!isProvided(value)) return '';
    const centimeters = numeric(value);
    if (!Number.isFinite(centimeters)) return value;
    return Number(fromCentimeters(centimeters, dimensionUnit).toFixed(2));
  }

  function updateDimension(field: 'lengthCm' | 'widthCm' | 'heightCm', value: NumericValue) {
    if (!isProvided(value)) {
      updateField(field, '');
      return;
    }
    updateField(field, toCentimeters(numeric(value), dimensionUnit));
  }

  function buildInput(fulfillmentChannel: FulfillmentChannel): ContributionInput {
    return {
      fulfillmentChannel,
      stepLevel,
      sellingPrice: numeric(form.sellingPrice),
      productCost: numeric(form.productCost),
      actualWeightGrams: numeric(form.actualWeightGrams),
      lengthCm: numeric(form.lengthCm),
      widthCm: numeric(form.widthCm),
      heightCm: numeric(form.heightCm),
      packagingCost: numeric(form.packagingCost),
      advertisingCost: 0,
      otherCost: numeric(form.otherCost),
      returnRatePercentage: numeric(form.returnRatePercentage),
      fbaShippingZone,
      fbaStorageDays: numeric(form.fbaStorageDays),
      fbaInboundCost: numeric(form.fbaInboundCost),
      fbaShipInOwnContainer,
    };
  }

  const input = buildInput('easyShip');
  const fbaInput = buildInput('fba');
  const hasAllRequiredValues = [
    form.sellingPrice,
    form.productCost,
    form.actualWeightGrams,
    form.lengthCm,
    form.widthCm,
    form.heightCm,
  ].every(isProvided);
  let estimate: ContributionEstimate | null = null;
  let easyShipComparison: ContributionEstimate | null = null;
  let fbaComparison: ContributionEstimate | null = null;
  let error: { title: string; messages: string[] } | null = null;

  if (hasAllRequiredValues) {
    const validationErrors = validateContributionInput(input);
    if (validationErrors.length > 0) {
      error = { title: 'Check the calculator inputs', messages: validationErrors };
    } else {
      const heavyBulky = checkHeavyBulky({
        ...fbaInput,
        actualWeightGrams:
          fbaInput.actualWeightGrams +
          (!fbaInput.fbaShipInOwnContainer ? FBA_RATE_CARD.defaultPackagingWeightGrams : 0),
      });
      if (heavyBulky.isHeavyBulky) {
        error = {
          title: 'Heavy & Bulky rate required',
          messages: [
            `This package is outside the published standard-size rate card because ${heavyBulky.reasons.join(
              ' and ',
            )}.`,
            'Check the applicable rate in Seller Central before pricing this order.',
          ],
        };
      } else {
        easyShipComparison = calculateContribution(input);
        fbaComparison = calculateContribution(fbaInput);
        estimate = easyShipComparison;
      }
    }
  }

  function reset() {
    const next = savedProduct ? calculatorStateFromProduct(savedProduct) : initialCalculatorState;
    setProductName(next.name);
    setStepLevel(next.stepLevel);
    setFbaShippingZone(next.fbaShippingZone);
    setFbaShipInOwnContainer(next.fbaShipInOwnContainer);
    setDimensionUnit(next.dimensionUnit);
    setForm(next.form);
  }

  const adjacentScenarios = estimate
    ? getAdjacentWeightSlabScenarios(estimate.chargeableWeightKg)
    : null;
  const dimensionThresholds =
    estimate && fbaComparison
      ? {
          lengthCm: calculateDimensionThresholds(input, 'lengthCm', dimensionUnit),
          widthCm: calculateDimensionThresholds(input, 'widthCm', dimensionUnit),
          heightCm: calculateDimensionThresholds(input, 'heightCm', dimensionUnit),
        }
      : null;
  const targetPrices =
    easyShipComparison && fbaComparison
      ? marginTargets.map((targetMargin) => ({
          targetMargin,
          easyShip: calculateMarginPriceTarget(input, targetMargin),
          fba: calculateMarginPriceTarget(fbaInput, targetMargin),
        }))
      : null;
  const costPerClick = numeric(form.costPerClick);
  const customConversionRatePercentage = numeric(form.customConversionRatePercentage);
  const hasValidAdvertisingInputs =
    Number.isFinite(costPerClick) &&
    costPerClick >= 0 &&
    Number.isFinite(customConversionRatePercentage) &&
    customConversionRatePercentage > 0 &&
    customConversionRatePercentage <= 100;
  const advertisingScenarios =
    easyShipComparison && fbaComparison && hasValidAdvertisingInputs
      ? [...STANDARD_CONVERSION_RATES, customConversionRatePercentage].map(
          (conversionRatePercentage, index) => ({
            key: index === STANDARD_CONVERSION_RATES.length ? 'custom' : String(index),
            label:
              index === STANDARD_CONVERSION_RATES.length
                ? `Custom (${conversionRatePercentage}%)`
                : `${conversionRatePercentage}%`,
            isCustom: index === STANDARD_CONVERSION_RATES.length,
            easyShip: calculateAdvertisingScenario(
              costPerClick,
              conversionRatePercentage,
              input.sellingPrice,
              easyShipComparison,
            ),
            fba: calculateAdvertisingScenario(
              costPerClick,
              conversionRatePercentage,
              input.sellingPrice,
              fbaComparison,
            ),
          }),
        )
      : null;

  const marginColor =
    estimate && estimate.expectedContribution < 0
      ? 'red'
      : estimate && estimate.expectedMargin < 10
        ? 'orange'
        : 'green';
  const canSave =
    productName.trim().length > 0 &&
    easyShipComparison !== null &&
    fbaComparison !== null &&
    hasValidAdvertisingInputs;

  async function saveProduct() {
    if (!canSave) {
      notifications.show({
        color: 'red',
        message: productName.trim()
          ? 'Complete the calculator with valid standard-size values before saving.'
          : 'Enter a product name before saving.',
      });
      return;
    }

    setSaving(true);
    const payload = amazonProductPayload({
      name: productName,
      stepLevel,
      fbaShippingZone,
      fbaShipInOwnContainer,
      dimensionUnit,
      form,
    });
    const response = savedProduct
      ? await api.api.api.v1['amazon-products']({ id: savedProduct.id }).put(payload)
      : await api.api.api.v1['amazon-products'].post(payload);
    setSaving(false);

    if (response.error || !response.data) {
      notifications.show({
        color: 'red',
        message:
          response.status === 409
            ? 'A saved product with this name already exists.'
            : responseMessage(response.error, 'The product could not be saved.'),
      });
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ['amazon-products'] });
    notifications.show({
      color: 'green',
      message: savedProduct ? `${response.data.name} updated.` : `${response.data.name} saved.`,
    });
    if (!savedProduct) {
      void navigate(`/amazon?product=${response.data.id}`, { replace: true });
    }
  }

  function confirmDelete() {
    if (!savedProduct) return;
    modals.openConfirmModal({
      title: 'Delete saved product?',
      children: `“${savedProduct.name}” will be permanently deleted. This cannot be undone.`,
      labels: { confirm: 'Delete product', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const response = await api.api.api.v1['amazon-products']({
          id: savedProduct.id,
        }).delete();
        if (response.error && response.status !== 404) {
          notifications.show({
            color: 'red',
            message: responseMessage(response.error, 'The product could not be deleted.'),
          });
          return;
        }
        await queryClient.invalidateQueries({ queryKey: ['amazon-products'] });
        notifications.show({ color: 'green', message: `${savedProduct.name} deleted.` });
        void navigate('/amazon/products', { replace: true });
      },
    });
  }

  return (
    <Stack gap='xl' maw={1380} mx='auto'>
      <Group justify='space-between' align='flex-end'>
        <div>
          <Group gap='sm' mb={6}>
            <Title order={1}>Amazon Selling</Title>
            <Badge color='orange' variant='light' size='lg'>
              Amazon.in
            </Badge>
          </Group>
          <Text c='dimmed'>
            Compare Easy Ship and FBA charges and expected per-order contribution before pricing.
          </Text>
        </div>
        <Group>
          {canManageProducts && (
            <Button
              variant='default'
              leftSection={<IconListDetails size={17} />}
              onClick={() => void navigate('/amazon/products')}
            >
              Saved products
            </Button>
          )}
          <Badge variant='outline' color='gray' size='lg'>
            Rates effective 16 Mar 2026
          </Badge>
        </Group>
      </Group>

      <Alert
        icon={<IconInfoCircle size={20} />}
        title='One input set, two live estimates'
        color='blue'
        variant='light'
      >
        Easy Ship uses Amazon’s flat rate. FBA uses the selected destination zone and also adds
        pick-and-pack and inventory storage fees. Both results update from the same product and
        order inputs.
      </Alert>

      <Grid gap='xl'>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Paper withBorder radius='lg' p={{ base: 'md', sm: 'xl' }} className='data-card'>
            <Stack gap='lg'>
              <Group justify='space-between' align='flex-start'>
                <div>
                  <Group gap='xs'>
                    <ThemeIcon variant='light' size='lg'>
                      <IconCalculator size={19} />
                    </ThemeIcon>
                    <Title order={2}>Wall Art fulfilment estimator</Title>
                  </Group>
                  <Text c='dimmed' size='sm' mt={5}>
                    Standard-size Wall Art · INR per order
                  </Text>
                  {canManageProducts && savedProduct && (
                    <Text c='dimmed' size='xs' mt={3}>
                      Last saved {new Date(savedProduct.updatedAt).toLocaleString('en-IN')} by{' '}
                      {savedProduct.updatedBy.email}
                    </Text>
                  )}
                </div>
                <Group gap='xs'>
                  {canManageProducts && savedProduct && (
                    <>
                      <Button
                        type='button'
                        variant='subtle'
                        leftSection={<IconPlus size={16} />}
                        onClick={() => void navigate('/amazon')}
                      >
                        New
                      </Button>
                      <Button
                        type='button'
                        variant='subtle'
                        color='red'
                        leftSection={<IconTrash size={16} />}
                        onClick={confirmDelete}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <Button
                    type='button'
                    variant='subtle'
                    color='gray'
                    leftSection={<IconRefresh size={16} />}
                    onClick={reset}
                  >
                    {savedProduct ? 'Revert' : 'Reset'}
                  </Button>
                </Group>
              </Group>

              {canManageProducts && (
                <TextInput
                  label='Product name'
                  description='Unique name in the shared saved-product catalog'
                  placeholder='For example, Abstract Orange Wall Art'
                  required
                  maxLength={150}
                  value={productName}
                  onChange={(event) => setProductName(event.currentTarget.value)}
                />
              )}

              <Divider label='Amazon fees' labelPosition='left' />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label='Amazon STEP level'
                  description='New sellers start at Standard'
                  required
                  value={stepLevel}
                  data={[
                    { value: 'standard', label: 'Standard' },
                    { value: 'basic', label: 'Basic' },
                    { value: 'advanced', label: 'Advanced' },
                    { value: 'premium', label: 'Premium' },
                  ]}
                  onChange={(value) => {
                    if (!value) return;
                    setStepLevel(value as StepLevel);
                  }}
                />
                <NumberInput
                  label='Selling price'
                  description='GST-inclusive item price, including any customer shipping charge'
                  prefix='₹ '
                  min={0}
                  decimalScale={2}
                  required
                  value={form.sellingPrice}
                  onChange={(value) => updateField('sellingPrice', value)}
                />
              </SimpleGrid>
              <Divider label='FBA assumptions' labelPosition='left' />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label='FBA shipping zone'
                  description='Local uses the regional estimate'
                  value={fbaShippingZone}
                  data={[
                    { value: 'regional', label: 'Regional / local' },
                    { value: 'national', label: 'National' },
                  ]}
                  onChange={(value) => {
                    if (value) setFbaShippingZone(value as FbaShippingZone);
                  }}
                />
                <Select
                  label='FBA packing mode'
                  description='Standard items get Amazon’s 100 g allowance'
                  value={fbaShipInOwnContainer ? 'sioc' : 'amazon'}
                  data={[
                    { value: 'amazon', label: 'Amazon packaging (+100 g)' },
                    { value: 'sioc', label: 'Ship in Own Container (+0 g)' },
                  ]}
                  onChange={(value) => setFbaShipInOwnContainer(value === 'sioc')}
                />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <NumberInput
                  label='Average FBA storage'
                  description={`₹${FBA_RATE_CARD.storagePerCubicFootMonth}/ft³/month`}
                  suffix=' days'
                  min={0}
                  max={3650}
                  decimalScale={1}
                  value={form.fbaStorageDays}
                  onChange={(value) => updateField('fbaStorageDays', value)}
                />
                <NumberInput
                  label='Inbound cost per unit'
                  description='Freight/prep cost to send inventory to Amazon'
                  prefix='₹ '
                  min={0}
                  decimalScale={2}
                  value={form.fbaInboundCost}
                  onChange={(value) => updateField('fbaInboundCost', value)}
                />
              </SimpleGrid>
              <Alert color='orange' variant='light' icon={<IconInfoCircle size={18} />}>
                <Text size='sm' fw={700}>
                  Category locked to {WALL_ART_FEE_CARD.category}
                </Text>
                <Text size='sm'>
                  Referral, Easy Ship, and FC closing fees are calculated automatically from the
                  selling price using Amazon’s rates effective 16 March 2026.
                </Text>
              </Alert>

              <Divider label='Customer returns' labelPosition='left' />
              <NumberInput
                label='Expected customer return rate'
                description='Delivered orders later returned by the customer; excludes RTO and rejected deliveries'
                suffix=' %'
                min={0}
                max={100}
                decimalScale={2}
                value={form.returnRatePercentage}
                onChange={(value) => updateField('returnRatePercentage', value)}
              />
              <Text size='xs' c='dimmed'>
                The estimator assumes returned Wall Art comes back sellable, so product cost is
                recovered. Packaging, original Easy Ship, and closing fees remain costs. CPC spend
                is applied separately in the advertising scenarios below.
              </Text>

              <Divider label='Packed product' labelPosition='left' />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <NumberInput
                  label='Actual packaged/unit weight'
                  description='FBA adds 100 g unless SIOC is selected'
                  suffix=' g'
                  min={0}
                  decimalScale={2}
                  required
                  value={form.actualWeightGrams}
                  onChange={(value) => updateField('actualWeightGrams', value)}
                />
                <NumberInput
                  label='Product cost'
                  prefix='₹ '
                  min={0}
                  decimalScale={2}
                  required
                  value={form.productCost}
                  onChange={(value) => updateField('productCost', value)}
                />
              </SimpleGrid>
              <Group justify='space-between' align='center'>
                <div>
                  <Text size='sm' fw={600}>
                    Package dimension unit
                  </Text>
                  <Text size='xs' c='dimmed'>
                    Applies to length, width, height, and slab guidance
                  </Text>
                </div>
                <SegmentedControl
                  value={dimensionUnit}
                  data={[
                    { value: 'cm', label: 'CM' },
                    { value: 'inch', label: 'Inch' },
                  ]}
                  onChange={(value) => setDimensionUnit(value as DimensionUnit)}
                />
              </Group>
              <SimpleGrid cols={{ base: 1, xs: 3 }}>
                <Stack gap={5}>
                  {dimensionThresholds && (
                    <DimensionThresholdHint
                      direction='previous'
                      outcomes={dimensionThresholds.lengthCm.previous}
                    />
                  )}
                  <NumberInput
                    label='Length'
                    suffix={dimensionUnit === 'cm' ? ' cm' : ' in'}
                    min={0}
                    decimalScale={2}
                    required
                    value={displayedDimension(form.lengthCm)}
                    onChange={(value) => updateDimension('lengthCm', value)}
                  />
                  {dimensionThresholds && (
                    <DimensionThresholdHint
                      direction='next'
                      outcomes={dimensionThresholds.lengthCm.next}
                    />
                  )}
                </Stack>
                <Stack gap={5}>
                  {dimensionThresholds && (
                    <DimensionThresholdHint
                      direction='previous'
                      outcomes={dimensionThresholds.widthCm.previous}
                    />
                  )}
                  <NumberInput
                    label='Width'
                    suffix={dimensionUnit === 'cm' ? ' cm' : ' in'}
                    min={0}
                    decimalScale={2}
                    required
                    value={displayedDimension(form.widthCm)}
                    onChange={(value) => updateDimension('widthCm', value)}
                  />
                  {dimensionThresholds && (
                    <DimensionThresholdHint
                      direction='next'
                      outcomes={dimensionThresholds.widthCm.next}
                    />
                  )}
                </Stack>
                <Stack gap={5}>
                  {dimensionThresholds && (
                    <DimensionThresholdHint
                      direction='previous'
                      outcomes={dimensionThresholds.heightCm.previous}
                    />
                  )}
                  <NumberInput
                    label='Height'
                    suffix={dimensionUnit === 'cm' ? ' cm' : ' in'}
                    min={0}
                    decimalScale={2}
                    required
                    value={displayedDimension(form.heightCm)}
                    onChange={(value) => updateDimension('heightCm', value)}
                  />
                  {dimensionThresholds && (
                    <DimensionThresholdHint
                      direction='next'
                      outcomes={dimensionThresholds.heightCm.next}
                    />
                  )}
                </Stack>
              </SimpleGrid>
              <Text size='xs' c='dimmed'>
                Use expected outbound package dimensions for both methods. Amazon can measure
                different FBA dimensions after packing, so reconcile against Seller Central.
              </Text>

              <Divider label='Optional per-order costs' labelPosition='left' />
              <SimpleGrid cols={{ base: 1, xs: 2 }}>
                <NumberInput
                  label='Packaging'
                  prefix='₹ '
                  min={0}
                  decimalScale={2}
                  value={form.packagingCost}
                  onChange={(value) => updateField('packagingCost', value)}
                />
                <NumberInput
                  label='Other'
                  prefix='₹ '
                  min={0}
                  decimalScale={2}
                  value={form.otherCost}
                  onChange={(value) => updateField('otherCost', value)}
                />
              </SimpleGrid>

              <Divider label='Advertising scenarios' labelPosition='left' />
              <SimpleGrid cols={{ base: 1, xs: 2 }}>
                <NumberInput
                  label='Cost per click (CPC)'
                  description='Average Amazon Ads spend for one click'
                  prefix='₹ '
                  min={0}
                  decimalScale={2}
                  required
                  value={form.costPerClick}
                  onChange={(value) => updateField('costPerClick', value)}
                />
                <NumberInput
                  label='Custom conversion rate'
                  description='Shown alongside the 2%, 5%, 10%, and 15% scenarios'
                  suffix=' %'
                  min={0.01}
                  max={100}
                  decimalScale={2}
                  required
                  value={form.customConversionRatePercentage}
                  onChange={(value) => updateField('customConversionRatePercentage', value)}
                />
              </SimpleGrid>
              <Text size='xs' c='dimmed'>
                Scenarios use 100 ad clicks. They treat conversion rates as planning assumptions,
                not Amazon benchmarks.
              </Text>

              {error && (
                <Alert color='red' icon={<IconAlertTriangle size={20} />} title={error.title}>
                  <List size='sm' spacing={3}>
                    {error.messages.map((message) => (
                      <List.Item key={message}>{message}</List.Item>
                    ))}
                  </List>
                </Alert>
              )}

              {canManageProducts ? (
                <Group justify='space-between'>
                  <Text size='xs' c='dimmed'>
                    Results update automatically. Changes are stored only when you save.
                  </Text>
                  <Button
                    type='button'
                    leftSection={<IconDeviceFloppy size={17} />}
                    loading={saving}
                    disabled={!canSave}
                    onClick={() => void saveProduct()}
                  >
                    {savedProduct ? 'Save changes' : 'Save product'}
                  </Button>
                </Group>
              ) : (
                <Text size='xs' c='dimmed'>
                  Results update automatically. Guest calculations are not saved.
                </Text>
              )}
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Paper
            withBorder
            radius='lg'
            p={{ base: 'md', sm: 'xl' }}
            className='data-card amazon-result-card'
          >
            <Group justify='space-between' mb='md'>
              <div>
                <Title order={2}>Easy Ship estimate</Title>
                <Text c='dimmed' size='sm'>
                  Contribution before CPC advertising, not accounting profit
                </Text>
              </div>
              {estimate && (
                <Badge size='lg' color={marginColor}>
                  {estimate.expectedMargin.toFixed(1)}% expected margin
                </Badge>
              )}
            </Group>

            {!estimate ? (
              <Stack align='center' justify='center' ta='center' py={70} px='lg'>
                <ThemeIcon size={58} radius='xl' variant='light' color='gray'>
                  <IconTruckDelivery size={30} />
                </ThemeIcon>
                <div>
                  <Text fw={700}>Enter your order details</Text>
                  <Text c='dimmed' size='sm' maw={340} mt={4}>
                    Complete the required fields. The breakdown will then update live as any value
                    changes.
                  </Text>
                </div>
              </Stack>
            ) : (
              <Stack gap='lg'>
                <SimpleGrid cols={{ base: 1, xs: 2 }}>
                  <Paper p='md' radius='md' bg='var(--mantine-color-blue-0)'>
                    <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                      Expected contribution
                    </Text>
                    <Text size='xl' fw={800} c={marginColor}>
                      {moneyFormatter.format(estimate.expectedContribution)}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      After {estimate.returnRatePercentage}% customer returns, before CPC spend
                    </Text>
                  </Paper>
                  <Paper p='md' radius='md' bg='var(--mantine-color-gray-0)'>
                    <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                      Delivered contribution
                    </Text>
                    <Text size='xl' fw={800}>
                      {moneyFormatter.format(estimate.deliveredContribution)}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      {estimate.deliveredMargin.toFixed(1)}% of net product revenue
                    </Text>
                  </Paper>
                </SimpleGrid>

                <Table withRowBorders={false} className='amazon-result-table'>
                  <Table.Tbody>
                    <ResultRow
                      label='Actual weight'
                      value={`${numberFormatter.format(estimate.actualWeightKg)} kg`}
                    />
                    <ResultRow
                      label='Volumetric weight'
                      value={`${numberFormatter.format(estimate.volumetricWeightKg)} kg`}
                    />
                    <ResultRow
                      label='Chargeable weight'
                      value={`${numberFormatter.format(estimate.chargeableWeightKg)} kg`}
                      strong
                    />
                    <ResultRow label='Applied slab' value={estimate.weightSlab} />
                    {estimate.fulfillmentChannel === 'easyShip' && (
                      <>
                        <ResultRow
                          label='Easy Ship fee before low-price discount'
                          value={moneyFormatter.format(estimate.easyShipBaseFee)}
                        />
                        <ResultRow
                          label='Under-₹300 Easy Ship discount'
                          value={
                            estimate.easyShipDiscount > 0
                              ? moneyFormatter.format(-estimate.easyShipDiscount)
                              : 'Not eligible'
                          }
                        />
                      </>
                    )}
                    <ResultRow
                      label={
                        estimate.fulfillmentChannel === 'fba'
                          ? `FBA weight handling (${estimate.fbaShippingZone})`
                          : 'Easy Ship fee after discount'
                      }
                      value={moneyFormatter.format(estimate.easyShipFee)}
                      strong
                    />
                    {estimate.fulfillmentChannel === 'fba' && (
                      <>
                        <ResultRow
                          label='FBA packaging weight allowance'
                          value={`${numberFormatter.format(estimate.fbaPackagingWeightKg)} kg`}
                        />
                        <ResultRow
                          label='FBA pick & pack'
                          value={moneyFormatter.format(estimate.fbaPickPackFee)}
                        />
                        <ResultRow
                          label={`FBA storage (${numberFormatter.format(
                            estimate.fbaStorageCubicFeet,
                          )} ft³)`}
                          value={moneyFormatter.format(estimate.fbaStorageFee)}
                        />
                        <ResultRow
                          label='FBA inbound cost'
                          value={moneyFormatter.format(estimate.fbaInboundCost)}
                        />
                      </>
                    )}
                    <ResultRow
                      label={`Wall Art referral fee (${estimate.referralPercentage}%)`}
                      value={moneyFormatter.format(estimate.referralFee)}
                    />
                    <ResultRow
                      label={`${estimate.fulfillmentChannel === 'fba' ? 'FC' : 'Easy Ship'} closing fee (${estimate.closingFeeBand})`}
                      value={moneyFormatter.format(estimate.closingFee)}
                    />
                    <ResultRow
                      label='GST-inclusive selling price'
                      value={moneyFormatter.format(input.sellingPrice)}
                    />
                    <ResultRow
                      label={`Product GST (${WALL_ART_PRODUCT_GST_RATE * 100}%, included in price)`}
                      value={moneyFormatter.format(estimate.productGst)}
                    />
                    <ResultRow
                      label='Net product revenue'
                      value={moneyFormatter.format(estimate.netProductRevenue)}
                      strong
                    />
                    <ResultRow
                      label='Amazon fee subtotal'
                      value={moneyFormatter.format(estimate.amazonFeeSubtotal)}
                      strong
                    />
                    <ResultRow
                      label='GST on Amazon fees (18%)'
                      value={moneyFormatter.format(estimate.amazonFeeGst)}
                    />
                    <ResultRow
                      label='Amazon cash fees'
                      value={moneyFormatter.format(estimate.amazonCashFees)}
                    />
                    <ResultRow
                      label='Amazon fee GST utilized now'
                      value={moneyFormatter.format(-estimate.amazonFeeGstUtilized)}
                    />
                    <ResultRow
                      label='GST payable after Amazon fee ITC'
                      value={moneyFormatter.format(estimate.gstPayableAfterAmazonFeeItc)}
                      strong
                    />
                    <ResultRow
                      label='Amazon fee GST carried forward'
                      value={moneyFormatter.format(estimate.amazonFeeGstCarryForward)}
                    />
                    <ResultRow
                      label='Product + optional costs'
                      value={moneyFormatter.format(estimate.nonAmazonCosts)}
                    />
                    <ResultRow
                      label='Contribution when delivered'
                      value={moneyFormatter.format(estimate.deliveredContribution)}
                      strong
                    />
                  </Table.Tbody>
                </Table>

                <Divider label='Customer-return impact' labelPosition='left' />
                <SimpleGrid cols={{ base: 1, xs: 2 }}>
                  <Paper p='md' radius='md' bg='var(--mantine-color-orange-0)'>
                    <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                      Returned-order contribution
                    </Text>
                    <Text size='xl' fw={800} c='orange'>
                      {moneyFormatter.format(estimate.returnedOrderContribution)}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      Assumes product is recovered sellable
                    </Text>
                  </Paper>
                  <Paper p='md' radius='md' bg='var(--mantine-color-red-0)'>
                    <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                      Expected return impact
                    </Text>
                    <Text size='xl' fw={800} c='red'>
                      −{moneyFormatter.format(estimate.expectedReturnCostPerOrder)}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      Average reduction per placed order
                    </Text>
                  </Paper>
                </SimpleGrid>
                <Table withRowBorders={false} className='amazon-result-table'>
                  <Table.Tbody>
                    <ResultRow
                      label={`${estimate.fulfillmentChannel === 'fba' ? 'FBA weight-handling' : 'Original Easy Ship'} fee retained`}
                      value={moneyFormatter.format(estimate.easyShipFee)}
                    />
                    <ResultRow
                      label='Closing fee retained'
                      value={moneyFormatter.format(estimate.closingFee)}
                    />
                    {estimate.fulfillmentChannel === 'fba' && (
                      <>
                        <ResultRow
                          label='Pick & pack retained'
                          value={moneyFormatter.format(estimate.fbaPickPackFee)}
                        />
                        <ResultRow
                          label='Allocated storage retained'
                          value={moneyFormatter.format(estimate.fbaStorageFee)}
                        />
                      </>
                    )}
                    <ResultRow
                      label={`Refund fee (${estimate.refundFeeBand}, ${stepLevel})`}
                      value={moneyFormatter.format(estimate.refundFee)}
                      strong
                    />
                    <ResultRow
                      label='Return Amazon fee subtotal'
                      value={moneyFormatter.format(estimate.returnedOrderAmazonFeeSubtotal)}
                    />
                    <ResultRow
                      label='GST on return Amazon fees (18%)'
                      value={moneyFormatter.format(estimate.returnedOrderAmazonFeeGst)}
                    />
                    <ResultRow
                      label='Return Amazon cash fees'
                      value={moneyFormatter.format(estimate.returnedOrderAmazonFees)}
                    />
                    <ResultRow
                      label='Return fee GST carried forward'
                      value={moneyFormatter.format(estimate.returnedOrderAmazonFeeGstCarryForward)}
                      strong
                    />
                  </Table.Tbody>
                </Table>

                <Divider label='Weight slab sensitivity' labelPosition='left' />
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <WeightScenarioCard
                    direction='previous'
                    scenario={adjacentScenarios?.previous ?? null}
                    estimate={estimate}
                    input={input}
                  />
                  <WeightScenarioCard
                    direction='next'
                    scenario={adjacentScenarios?.next ?? null}
                    estimate={estimate}
                    input={input}
                  />
                </SimpleGrid>

                <Alert color='yellow' variant='light' icon={<IconInfoCircle size={18} />}>
                  Assumes 5% product GST is included in the Wall Art selling price. Amazon fee GST
                  is utilized only up to this order’s product GST; excess credit is carried forward
                  and excluded from contribution. Enter product and other costs including their GST;
                  their ITC is not calculated. Customer returns are probability-weighted estimates;
                  TCS and income tax are excluded.
                </Alert>
              </Stack>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {advertisingScenarios && (
        <Paper withBorder radius='lg' p={{ base: 'md', sm: 'xl' }} className='data-card'>
          <Stack gap='lg'>
            <Group justify='space-between' align='flex-start'>
              <div>
                <Title order={2}>CPC and conversion scenarios</Title>
                <Text c='dimmed' size='sm' mt={4}>
                  GST-exclusive revenue and expected profit for 100 ad clicks. Profit uses
                  return-adjusted contribution and subtracts the full click spend.
                </Text>
              </div>
              <Badge variant='light' color='violet'>
                {moneyFormatter.format(costPerClick * 100)} spend · 100 clicks
              </Badge>
            </Group>
            <Table.ScrollContainer minWidth={900}>
              <Table verticalSpacing='sm' horizontalSpacing='md' highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Conversion</Table.Th>
                    <Table.Th ta='right'>Expected orders</Table.Th>
                    <Table.Th ta='right'>Ad cost / order</Table.Th>
                    <Table.Th ta='right'>Gross revenue</Table.Th>
                    <Table.Th ta='right'>Net revenue</Table.Th>
                    <Table.Th ta='right'>Easy Ship profit</Table.Th>
                    <Table.Th ta='right'>FBA profit</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {advertisingScenarios.map((scenario) => (
                    <Table.Tr
                      key={scenario.key}
                      bg={scenario.isCustom ? 'var(--mantine-color-blue-0)' : undefined}
                    >
                      <Table.Td>
                        <Group gap='xs' wrap='nowrap'>
                          <Text fw={scenario.isCustom ? 700 : 600}>{scenario.label}</Text>
                          {scenario.isCustom && <Badge size='xs'>Input</Badge>}
                        </Group>
                      </Table.Td>
                      <Table.Td ta='right'>
                        {numberFormatter.format(scenario.easyShip.expectedOrders)}
                      </Table.Td>
                      <Table.Td ta='right'>
                        {moneyFormatter.format(scenario.easyShip.advertisingCostPerOrder)}
                      </Table.Td>
                      <Table.Td ta='right'>
                        {moneyFormatter.format(scenario.easyShip.grossRevenue)}
                      </Table.Td>
                      <Table.Td ta='right'>
                        {moneyFormatter.format(scenario.easyShip.netRevenue)}
                      </Table.Td>
                      <Table.Td ta='right'>
                        <Text fw={700} c={scenario.easyShip.expectedProfit < 0 ? 'red' : 'green.7'}>
                          {moneyFormatter.format(scenario.easyShip.expectedProfit)}
                        </Text>
                        <Text size='xs' c='dimmed'>
                          {scenario.easyShip.expectedProfitMargin.toFixed(1)}% of revenue
                        </Text>
                      </Table.Td>
                      <Table.Td ta='right'>
                        <Text fw={700} c={scenario.fba.expectedProfit < 0 ? 'red' : 'green.7'}>
                          {moneyFormatter.format(scenario.fba.expectedProfit)}
                        </Text>
                        <Text size='xs' c='dimmed'>
                          {scenario.fba.expectedProfitMargin.toFixed(1)}% of revenue
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Alert color='blue' variant='light' icon={<IconInfoCircle size={18} />}>
              Gross revenue is attributed orders × GST-inclusive selling price. Net revenue removes
              embedded product GST. Expected profit applies the entered customer-return rate through
              the calculator’s contribution model; it is a planning contribution, not accounting net
              profit.
            </Alert>
          </Stack>
        </Paper>
      )}

      {targetPrices && (
        <Paper withBorder radius='lg' p={{ base: 'md', sm: 'xl' }} className='data-card'>
          <Stack gap='lg'>
            <Group justify='space-between' align='flex-start'>
              <div>
                <Title order={2}>Target selling prices</Title>
                <Text c='dimmed' size='sm' mt={4}>
                  Minimum whole-rupee prices that reach each expected contribution margin after the
                  entered customer-return rate and all non-advertising costs. CPC spend is shown in
                  the conversion scenarios above.
                </Text>
              </div>
              <Badge variant='light' color='gray'>
                Current: {moneyFormatter.format(input.sellingPrice)}
              </Badge>
            </Group>
            <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }}>
              {targetPrices.map((target, index) => (
                <Paper key={target.targetMargin} withBorder radius='md' p='lg'>
                  <Group justify='space-between' mb='md'>
                    <Text fw={800}>{target.targetMargin}% margin</Text>
                    <ThemeIcon
                      variant='light'
                      color={['gray', 'blue', 'teal', 'violet'][index]}
                      radius='xl'
                    >
                      <IconArrowUp size={16} />
                    </ThemeIcon>
                  </Group>
                  <Stack gap='md'>
                    <MarginTargetMethod
                      label='Easy Ship'
                      target={target.easyShip}
                      currentPrice={input.sellingPrice}
                    />
                    <Divider />
                    <MarginTargetMethod
                      label='FBA'
                      target={target.fba}
                      currentPrice={input.sellingPrice}
                    />
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
            <Alert color='blue' variant='light' icon={<IconInfoCircle size={18} />}>
              Targets are solved separately across Amazon’s ₹300, ₹500, and ₹1,000 fee boundaries. A
              target is valid at the displayed price; changing across a fee boundary can change the
              margin abruptly.
            </Alert>
          </Stack>
        </Paper>
      )}

      {easyShipComparison && fbaComparison && (
        <Paper withBorder radius='lg' p={{ base: 'md', sm: 'xl' }} className='data-card'>
          <Stack gap='lg'>
            <div>
              <Title order={2}>Easy Ship vs FBA</Title>
              <Text c='dimmed' size='sm' mt={4}>
                Same Wall Art sale, product, dimensions, non-advertising costs, and customer-return
                rate. FBA uses the selected destination zone, packing mode, storage period, and
                inbound cost.
              </Text>
            </div>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              {[
                { label: 'Easy Ship', estimate: easyShipComparison, color: 'blue' },
                { label: 'Fulfillment by Amazon', estimate: fbaComparison, color: 'orange' },
              ].map((item) => (
                <Paper key={item.label} withBorder radius='md' p='lg'>
                  <Group justify='space-between' mb='md'>
                    <Text fw={800}>{item.label}</Text>
                    <Badge color={item.color} variant='light'>
                      {item.estimate.expectedMargin.toFixed(1)}% expected margin
                    </Badge>
                  </Group>
                  <Stack gap={7}>
                    <Group justify='space-between'>
                      <Text size='sm' c='dimmed'>
                        Expected contribution
                      </Text>
                      <Text fw={800}>
                        {moneyFormatter.format(item.estimate.expectedContribution)}
                      </Text>
                    </Group>
                    <Group justify='space-between'>
                      <Text size='sm' c='dimmed'>
                        Delivered contribution
                      </Text>
                      <Text fw={600}>
                        {moneyFormatter.format(item.estimate.deliveredContribution)}
                      </Text>
                    </Group>
                    <Group justify='space-between'>
                      <Text size='sm' c='dimmed'>
                        Amazon cash fees
                      </Text>
                      <Text fw={600}>{moneyFormatter.format(item.estimate.amazonCashFees)}</Text>
                    </Group>
                    <Group justify='space-between'>
                      <Text size='sm' c='dimmed'>
                        Return impact per order
                      </Text>
                      <Text fw={600} c='red'>
                        −{moneyFormatter.format(item.estimate.expectedReturnCostPerOrder)}
                      </Text>
                    </Group>
                  </Stack>
                  <Divider my='md' />
                  <Table withRowBorders={false} className='amazon-result-table'>
                    <Table.Tbody>
                      <ResultRow
                        label='Actual outbound weight'
                        value={`${numberFormatter.format(item.estimate.actualWeightKg)} kg`}
                      />
                      <ResultRow
                        label='Volumetric weight'
                        value={`${numberFormatter.format(item.estimate.volumetricWeightKg)} kg`}
                      />
                      <ResultRow
                        label='Chargeable weight'
                        value={`${numberFormatter.format(item.estimate.chargeableWeightKg)} kg`}
                        strong
                      />
                      <ResultRow label='Applied slab' value={item.estimate.weightSlab} />
                      {item.estimate.fulfillmentChannel === 'easyShip' && (
                        <>
                          <ResultRow
                            label='Shipping before low-price discount'
                            value={moneyFormatter.format(item.estimate.easyShipBaseFee)}
                          />
                          <ResultRow
                            label='Under-₹300 discount'
                            value={
                              item.estimate.easyShipDiscount
                                ? moneyFormatter.format(-item.estimate.easyShipDiscount)
                                : 'Not eligible'
                            }
                          />
                        </>
                      )}
                      <ResultRow
                        label={
                          item.estimate.fulfillmentChannel === 'fba'
                            ? `Weight handling (${item.estimate.fbaShippingZone})`
                            : 'Easy Ship fee'
                        }
                        value={moneyFormatter.format(item.estimate.easyShipFee)}
                        strong
                      />
                      {item.estimate.fulfillmentChannel === 'fba' && (
                        <>
                          <ResultRow
                            label='Amazon packaging allowance'
                            value={`${numberFormatter.format(
                              item.estimate.fbaPackagingWeightKg,
                            )} kg`}
                          />
                          <ResultRow
                            label='Pick & pack'
                            value={moneyFormatter.format(item.estimate.fbaPickPackFee)}
                          />
                          <ResultRow
                            label={`Storage (${numberFormatter.format(
                              item.estimate.fbaStorageCubicFeet,
                            )} ft³)`}
                            value={moneyFormatter.format(item.estimate.fbaStorageFee)}
                          />
                          <ResultRow
                            label='Inbound cost'
                            value={moneyFormatter.format(item.estimate.fbaInboundCost)}
                          />
                        </>
                      )}
                      <ResultRow
                        label={`Referral fee (${item.estimate.referralPercentage}%)`}
                        value={moneyFormatter.format(item.estimate.referralFee)}
                      />
                      <ResultRow
                        label={`Closing fee (${item.estimate.closingFeeBand})`}
                        value={moneyFormatter.format(item.estimate.closingFee)}
                      />
                      <ResultRow
                        label='Product GST included in price'
                        value={moneyFormatter.format(item.estimate.productGst)}
                      />
                      <ResultRow
                        label='Net product revenue'
                        value={moneyFormatter.format(item.estimate.netProductRevenue)}
                        strong
                      />
                      <ResultRow
                        label='Amazon fee subtotal'
                        value={moneyFormatter.format(item.estimate.amazonFeeSubtotal)}
                        strong
                      />
                      <ResultRow
                        label='Fee GST (18%)'
                        value={moneyFormatter.format(item.estimate.amazonFeeGst)}
                      />
                      <ResultRow
                        label='Amazon cash fees'
                        value={moneyFormatter.format(item.estimate.amazonCashFees)}
                      />
                      <ResultRow
                        label='Fee GST utilized now'
                        value={moneyFormatter.format(-item.estimate.amazonFeeGstUtilized)}
                        strong
                      />
                      <ResultRow
                        label='Fee GST carried forward'
                        value={moneyFormatter.format(item.estimate.amazonFeeGstCarryForward)}
                      />
                      <ResultRow
                        label='Product + non-Amazon costs'
                        value={moneyFormatter.format(item.estimate.nonAmazonCosts)}
                      />
                      <ResultRow
                        label={`Refund fee (${item.estimate.refundFeeBand})`}
                        value={moneyFormatter.format(item.estimate.refundFee)}
                      />
                      <ResultRow
                        label='Returned-order cash fees'
                        value={moneyFormatter.format(item.estimate.returnedOrderAmazonFees)}
                      />
                      <ResultRow
                        label='Returned-order contribution'
                        value={moneyFormatter.format(item.estimate.returnedOrderContribution)}
                        strong
                      />
                    </Table.Tbody>
                  </Table>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Paper>
      )}

      <Paper withBorder radius='lg' p={{ base: 'md', sm: 'xl' }} className='data-card'>
        <Stack gap='lg'>
          <div>
            <Title order={2}>Local, regional, and national explained</Title>
            <Text c='dimmed' size='sm' mt={4}>
              Easy Ship is flat-rate; FBA uses the destination classification.
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <div>
              <Badge variant='light' mb='xs'>
                Local
              </Badge>
              <Text size='sm'>Pickup and delivery happen within the same city.</Text>
            </div>
            <div>
              <Badge variant='light' color='violet' mb='xs'>
                Regional
              </Badge>
              <Text size='sm'>
                Pickup and delivery are in the same Amazon region, but not the same city.
              </Text>
            </div>
            <div>
              <Badge variant='light' color='orange' mb='xs'>
                National
              </Badge>
              <Text size='sm'>The shipment moves between Amazon’s defined regions.</Text>
            </div>
          </SimpleGrid>
          <Text size='sm' c='dimmed'>
            Amazon’s Easy Ship card publishes one flat fee per weight band and STEP level. Its
            current FBA standard-size card publishes Regional and National columns; this estimator
            treats Local as Regional because no separate Local column is quoted.
          </Text>
        </Stack>
      </Paper>

      <div>
        <Title order={2}>Current rules and official sources</Title>
        <Text c='dimmed' size='sm' mt={4} mb='md'>
          Wall Art referral, closing, shipping, refund, pick-and-pack, and storage fees are derived
          from these Amazon sources.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {officialSources.map((source) => (
            <Paper key={source.url} withBorder radius='lg' p='lg'>
              <Group justify='space-between' align='flex-start' wrap='nowrap'>
                <div>
                  <Anchor href={source.url} target='_blank' rel='noreferrer' fw={700}>
                    {source.title}
                  </Anchor>
                  <Text size='sm' c='dimmed' mt={5}>
                    {source.description}
                  </Text>
                </div>
                <IconExternalLink size={18} aria-hidden />
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </div>

      <Alert color='yellow' icon={<IconAlertTriangle size={20} />} title='Standard-size rates only'>
        Amazon also classifies certain appliance, furniture, sports, multi-box, and
        installation-required products as Heavy & Bulky. Even if the measurements pass this
        calculator, verify those products and their rates in Seller Central.
      </Alert>

      <Text size='xs' c='dimmed' ta='center'>
        Rate source:{' '}
        <Anchor href={EASY_SHIP_RATE_CARD.sourceUrl} target='_blank' rel='noreferrer'>
          Amazon India fees and pricing
        </Anchor>{' '}
        · Easy Ship and FBA rates effective {EASY_SHIP_RATE_CARD.effectiveDate}
      </Text>
    </Stack>
  );
}

function AuthenticatedAmazonPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('product');
  const productQuery = apiHook.useQuery(
    (client) =>
      client['amazon-products']({
        id: productId ?? '00000000-0000-4000-8000-000000000000',
      }).get(),
    { enabled: productId !== null, retry: false },
  );

  if (productId && productQuery.isLoading) {
    return (
      <Stack gap='lg' maw={1380} mx='auto'>
        <Title order={1}>Amazon Selling</Title>
        <Paper withBorder radius='lg' p='xl'>
          <Text c='dimmed'>Loading saved product…</Text>
        </Paper>
      </Stack>
    );
  }

  if (productId && (productQuery.isError || !productQuery.data)) {
    return (
      <Stack gap='lg' maw={900} mx='auto'>
        <Title order={1}>Amazon Selling</Title>
        <Alert color='red' icon={<IconAlertTriangle size={20} />} title='Product unavailable'>
          This saved product could not be loaded. It may have been deleted.
        </Alert>
        <Group>
          <Button onClick={() => void navigate('/amazon/products')}>View saved products</Button>
          <Button variant='default' onClick={() => void navigate('/amazon')}>
            New calculation
          </Button>
        </Group>
      </Stack>
    );
  }

  const product = productId ? (productQuery.data ?? null) : null;
  return (
    <AmazonCalculator
      key={product ? `${product.id}-${String(product.updatedAt)}` : 'new'}
      accessMode='authenticated'
      savedProduct={product}
    />
  );
}

export function AmazonPage({ accessMode }: { accessMode: AmazonCalculatorAccessMode }) {
  const [searchParams] = useSearchParams();

  if (accessMode === 'guest') {
    if (searchParams.has('product')) {
      return <Navigate to='/amazon' replace />;
    }
    return <AmazonCalculator accessMode='guest' savedProduct={null} />;
  }

  return <AuthenticatedAmazonPage />;
}
